import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  fetchCatalogoProdutosByFilterAsync,
  resetCatalogoProdutos,
} from '../../../../../../features/catalogo-produtos/catalogoProdutosSlice';
import QCXActionsManagerPageTemplate from '../../../../../../templates/actions-manager-page/QCXActionsManagerPageTemplate';
import CatalogoProdutosPartnumberDataReplicatorForm from './components/CatalogoProdutosPartnumberDataReplicatorForm';
import CatalogoProdutosPartnumberFiltroForm from './components/CatalogoProdutosPartnumberFiltroForm';
import breadcrumbs from './constants/breadcrumbs';
import { CatalogoProdutosPartnumberFiltroFormValues } from './types/CatalogoProdutosPartnumberFiltroFormValues';
import CatalogoProdutosContext from '../../../../../../contexts/registrations/catalogo-produtos/CatalogoProdutosContext';
import { isLoadingStatus } from '../../../../../../utils/store/store-utils';
import { DataGridControlConfigChange } from './types/DataGridControlConfigChange';
import CatalogoProdutosTransmitirModal from './components/CatalogoProdutosTransmitirModal';
import useTransmitirModal from './hooks/useTransmitirModal.hook';
import { CatalogoProdutosAtributosFormValues } from './types/CatalogoProdutosAtributosFormValues';
import getISODateOrTodaysISODate from '../utils/getISODateOrTodaysISODate';

type FetchCatalogosProdutosPageFilters = {
  page: number;
  pageSize: number;
  partnumber?: string;
};

export default function CatalogoProdutosFiltroRepassePage({ authInfo = {} }) {
  const dispatch = useDispatch();
  const { isModalOpen, openModal, closeModal, catalogoState, handleCatalogoStateChange, handleConfirm } =
    useTransmitirModal();

  const { status } = useContext(CatalogoProdutosContext);
  const isLoading = useMemo(() => isLoadingStatus(status), [status]);

  const [selectionIds, setSelectionIds] = useState<number[]>([]);
 const [filter, setFilter] = useState<CatalogoProdutosPartnumberFiltroFormValues>({});
  const filterRef = useRef<CatalogoProdutosPartnumberFiltroFormValues>({});

  const fetchCatalogosProdutos = useCallback(
    ({ page, pageSize, partnumber }: FetchCatalogosProdutosPageFilters) => {
      const appliedFilter = filterRef.current;
      const startDate = getISODateOrTodaysISODate(appliedFilter.insertionDateRangeStart);
      const endDate = getISODateOrTodaysISODate(appliedFilter.insertionDateRangeEnd);
      const dateRange =
        appliedFilter.insertionDateRangeStart || appliedFilter.insertionDateRangeEnd
          ? `${startDate};${endDate}`
          : undefined;
      const normalizedTags = appliedFilter.tags?.reduce((acc, prev) => `${prev}~${acc}`, '');
      const statusFilters = appliedFilter.statusFilters?.map((s) => s.value) ?? [];
      const partnumberParam = (appliedFilter.partnumbersTags?.join(';') || partnumber) ?? '';

      dispatch(
        fetchCatalogoProdutosByFilterAsync([
          { name: 'page', value: page },
          { name: 'size', value: pageSize },
          { name: 'ncm', value: appliedFilter.ncm?.id },
          { name: 'partnumber', value: partnumberParam },
          { name: 'insertionDateRange', value: dateRange },
          { name: 'clienteId', value: appliedFilter.cliente?.id },
          { name: 'tags', value: normalizedTags },
          { name: 'statuses', value: statusFilters },
        ])
      );
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(resetCatalogoProdutos());
  }, []);

  // Estável: lê o filtro aplicado via ref, então não muda de identidade quando o filtro muda.
  // Evita re-disparos do useEffect interno da DataGridControl (loop de recarregamento).
  const handleConfigChange = useCallback(
    ({ currentPage, pageSize, filterInputValue }: DataGridControlConfigChange) => {
      if (Object.keys(filterRef.current).length === 0) return;

      fetchCatalogosProdutos({ page: currentPage, pageSize, partnumber: filterInputValue });
    },
    [fetchCatalogosProdutos]
  );

  const handleFilterSubmit = useCallback(
    (values: CatalogoProdutosPartnumberFiltroFormValues) => {
      filterRef.current = { ...values };
      setFilter({ ...values });
      // Paginação do backend é 1-based (offset = (page - 1) * size); primeira página = 1.
      fetchCatalogosProdutos({ page: 1, pageSize: 10 });
    },
    [fetchCatalogosProdutos]
  );

  const handleTransmitirSubmit = (values: CatalogoProdutosAtributosFormValues) => {
    setSelectionIds(values?.meta?.selected?.map((s) => s.id) ?? []);

    openModal();
  };

  return (
    <QCXActionsManagerPageTemplate
      pageTitle="Repassar e transmitir"
      breadcrumbs={breadcrumbs}
      breadcrumbsProps={{ maxItems: 6 }}
      authInfo={authInfo}
      isLoading={isLoading}
      isUpdate={undefined}
      isConsult={undefined}
    >
      {() => (
        <>
          <CatalogoProdutosPartnumberFiltroForm handleSubmit={handleFilterSubmit} />

          <CatalogoProdutosPartnumberDataReplicatorForm
            isLoading={isLoading}
            onConfigChange={handleConfigChange}
            handleTransmitirSubmit={handleTransmitirSubmit}
            ncm={filter.ncm}
          />

          <CatalogoProdutosTransmitirModal
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            catalogoState={catalogoState}
            handleCatalogoStateChange={handleCatalogoStateChange}
            handleConfirm={() => handleConfirm(selectionIds)}
          />
        </>
      )}
    </QCXActionsManagerPageTemplate>
  );
}
